import React, { useCallback, useEffect, useState } from "react"
import { AddOfferHelperText, ChangeLanguageComponent } from "components/shared/ModalHelpers"
import RadioGroup from "components/shared/RadioGroup"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { LocalStorageService } from "services/api-service"
import { logout } from "store/auth"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button, { IconButton } from "ui/components/atoms/button"
import CheckboxesGroup from "ui/components/atoms/checkboxes-group"
import Input from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import Switch from "ui/components/atoms/switch"
import { H3, H4, P } from "ui/components/atoms/typography"
import Modal from "ui/components/molecules/modal"
import { CURRENCY_ARRAY } from "ui/data"
import { WORKTYPES_DRIVER_ARRAY } from "ui/data/OfferInformations"

const calculateAvgWorkDaysPerMonth = (workType: string): number => {
  if (workType === "mon_fri") return Math.round(30 * (5 / 7))
  const [workWeeks, homeWeeks] = workType.split("_").map(Number)
  const totalWeeks = workWeeks + homeWeeks
  const workPercentage = workWeeks / totalWeeks
  return Math.round(30 * workPercentage)
}

const getInputValue = (eventOrValue: any): string => {
  if (eventOrValue?.target?.value !== undefined) {
    return eventOrValue.target.value
  }
  if (eventOrValue?.value !== undefined) {
    return eventOrValue.value
  }
  return eventOrValue ?? ""
}

const AddOfferSalaryInfo = ({ state, onBack, onChange }: any) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(["add-offer", "common", "data"])

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    shouldFocusError: true,
    defaultValues: {
      name: state.name || "",
      paymentCurrency: state.paymentCurrency || "pln",
    },
  })

  const [switches, setSwitches] = useState({
    contract: state.switchesSalary?.contract ?? true,
    b2b: state.switchesSalary?.b2b ?? false,
  })

  const [selectedWorkTypes, setSelectedWorkTypes] = useState<string[]>(
    state.selectedWorkTypes || state.selectedContractWorkTypes || [],
  )

  const [contractPayments, setContractPayments] = useState<any>(state.contractPayments || {})
  const [b2bPayments, setB2BPayments] = useState<any>(state.b2bPayments || {})

  const [contractInputType, setContractInputType] = useState<"monthly" | "daily">(
    state.contractInputType || "monthly",
  )
  const [b2bInputType, setB2bInputType] = useState<"monthly" | "daily">(
    state.b2bInputType || "monthly",
  )

  const [contractBaseSalary, setContractBaseSalary] = useState<string>(
    state.contractBaseSalary || "",
  )
  const [contractAddons, setContractAddons] = useState<string[]>(state.contractAddons || [])
  const [contractAddonInput, setContractAddonInput] = useState<string>("")

  const paymentCurrency = watch("paymentCurrency")

  const onChangePaymentSwitches = ({ name, value }: { name: string; value: boolean }) => {
    let contract = switches.contract
    let b2b = switches.b2b

    if (name === "contract") {
      contract = value
      if (!value && !b2b) {
        b2b = true
      }
    } else {
      b2b = value
      if (!value && !contract) {
        contract = true
      }
    }

    setSwitches({
      contract,
      b2b,
    })
  }

  const handleWorkTypesChange = ({ value }: { value: string[] }) => {
    if (value.length > 0) {
      ;(clearErrors as any)("workTypes")
    }

    const removed = selectedWorkTypes.filter(wt => !value.includes(wt))

    const newContractPayments = { ...contractPayments }
    const newB2BPayments = { ...b2bPayments }

    removed.forEach(wt => {
      delete newContractPayments[wt]
      delete newB2BPayments[wt]
    })

    setContractPayments(newContractPayments)
    setB2BPayments(newB2BPayments)
    setSelectedWorkTypes(value)
  }

  const handleContractMonthlyChange = (workType: string, field: "from" | "to", value: string) => {
    ;(clearErrors as any)(`contract_${workType}`)

    const numValue = value === "" ? "" : parseFloat(value)
    const avgDays = state.category === "drivers" ? calculateAvgWorkDaysPerMonth(workType) : 30
    const dailyValue = numValue === "" ? "" : Math.round(Number(numValue) / avgDays)

    setContractPayments({
      ...contractPayments,
      [workType]: {
        ...contractPayments[workType],
        [`monthly${field === "from" ? "From" : "To"}`]: numValue,
        [`daily${field === "from" ? "From" : "To"}`]: dailyValue,
      },
    })
  }

  const handleContractDailyChange = (workType: string, field: "from" | "to", value: string) => {
    ;(clearErrors as any)(`contract_${workType}`)

    const numValue = value === "" ? "" : parseFloat(value)
    const avgDays = state.category === "drivers" ? calculateAvgWorkDaysPerMonth(workType) : 30
    const monthlyValue = numValue === "" ? "" : Math.round(Number(numValue) * avgDays)

    setContractPayments({
      ...contractPayments,
      [workType]: {
        ...contractPayments[workType],
        [`daily${field === "from" ? "From" : "To"}`]: numValue,
        [`monthly${field === "from" ? "From" : "To"}`]: monthlyValue,
      },
    })
  }

  const handleB2BMonthlyChange = (workType: string, field: "from" | "to", value: string) => {
    ;(clearErrors as any)(`b2b_${workType}`)

    const numValue = value === "" ? "" : parseFloat(value)
    const avgDays = state.category === "drivers" ? calculateAvgWorkDaysPerMonth(workType) : 30
    const dailyValue = numValue === "" ? "" : Math.round(Number(numValue) / avgDays)

    setB2BPayments({
      ...b2bPayments,
      [workType]: {
        ...b2bPayments[workType],
        [`monthly${field === "from" ? "From" : "To"}`]: numValue,
        [`daily${field === "from" ? "From" : "To"}`]: dailyValue,
      },
    })
  }

  const handleB2BDailyChange = (workType: string, field: "from" | "to", value: string) => {
    ;(clearErrors as any)(`b2b_${workType}`)

    const numValue = value === "" ? "" : parseFloat(value)
    const avgDays = state.category === "drivers" ? calculateAvgWorkDaysPerMonth(workType) : 30
    const monthlyValue = numValue === "" ? "" : Math.round(Number(numValue) * avgDays)

    setB2BPayments({
      ...b2bPayments,
      [workType]: {
        ...b2bPayments[workType],
        [`daily${field === "from" ? "From" : "To"}`]: numValue,
        [`monthly${field === "from" ? "From" : "To"}`]: monthlyValue,
      },
    })
  }

  const handleAddContractAddon = useCallback(() => {
    if (contractAddonInput.trim() !== "") {
      setContractAddons([...contractAddons, contractAddonInput.trim()])
      setContractAddonInput("")
    }
  }, [contractAddons, contractAddonInput])

  const handleRemoveContractAddon = useCallback(
    (index: number) => {
      setContractAddons(contractAddons.filter((_, i) => i !== index))
    },
    [contractAddons],
  )

  const validatePayments = useCallback((): boolean => {
    let isValid = true

    if (state.category === "drivers") {
      if (selectedWorkTypes.length === 0 && (switches.contract || switches.b2b)) {
        ;(setError as any)("workTypes", {
          type: "manual",
          message: t("required", { ns: "form" }),
        })
        isValid = false
      } else {
        ;(clearErrors as any)("workTypes")
      }

      if (switches.contract) {
        selectedWorkTypes.forEach(wt => {
          const payment = contractPayments[wt]
          if (!payment || !payment.monthlyFrom || !payment.monthlyTo) {
            ;(setError as any)(`contract_${wt}`, {
              type: "manual",
              message: t("required-all", { ns: "form" }),
            })
            isValid = false
          } else if (Number(payment.monthlyFrom) > Number(payment.monthlyTo)) {
            ;(setError as any)(`contract_${wt}`, {
              type: "manual",
              message: t("from-to-error", { ns: "form" }),
            })
            isValid = false
          } else {
            ;(clearErrors as any)(`contract_${wt}`)
          }
        })
      }

      if (switches.b2b) {
        selectedWorkTypes.forEach(wt => {
          const payment = b2bPayments[wt]
          if (!payment || !payment.monthlyFrom || !payment.monthlyTo) {
            ;(setError as any)(`b2b_${wt}`, {
              type: "manual",
              message: t("required-all", { ns: "form" }),
            })
            isValid = false
          } else if (Number(payment.monthlyFrom) > Number(payment.monthlyTo)) {
            ;(setError as any)(`b2b_${wt}`, {
              type: "manual",
              message: t("from-to-error", { ns: "form" }),
            })
            isValid = false
          } else {
            ;(clearErrors as any)(`b2b_${wt}`)
          }
        })
      }
    } else {
      if (switches.contract) {
        const payment = contractPayments["all"]
        if (!payment || !payment.monthlyFrom || !payment.monthlyTo) {
          ;(setError as any)("contract_all", {
            type: "manual",
            message: t("required-all", { ns: "form" }),
          })
          isValid = false
        } else if (Number(payment.monthlyFrom) > Number(payment.monthlyTo)) {
          ;(setError as any)("contract_all", {
            type: "manual",
            message: t("from-to-error", { ns: "form" }),
          })
          isValid = false
        } else {
          ;(clearErrors as any)("contract_all")
        }
      }

      if (switches.b2b) {
        const payment = b2bPayments["all"]
        if (!payment || !payment.monthlyFrom || !payment.monthlyTo) {
          ;(setError as any)("b2b_all", {
            type: "manual",
            message: t("required-all", { ns: "form" }),
          })
          isValid = false
        } else if (Number(payment.monthlyFrom) > Number(payment.monthlyTo)) {
          ;(setError as any)("b2b_all", {
            type: "manual",
            message: t("from-to-error", { ns: "form" }),
          })
          isValid = false
        } else {
          ;(clearErrors as any)("b2b_all")
        }
      }
    }

    return isValid
  }, [
    state.category,
    selectedWorkTypes,
    switches.contract,
    switches.b2b,
    contractPayments,
    b2bPayments,
    setError,
    clearErrors,
    t,
  ])

  const onSubmit = useCallback(
    async (el: any) => {
      if (!validatePayments()) {
        return
      }

      onChange({
        name: el.name,
        paymentCurrency: el.paymentCurrency,
        switchesSalary: switches,
        selectedWorkTypes,
        contractPayments,
        b2bPayments,
        contractInputType,
        b2bInputType,
        contractBaseSalary:
          state.category === "drivers" && switches.contract && contractBaseSalary
            ? contractBaseSalary
            : undefined,
        contractAddons:
          state.category === "drivers" && switches.contract ? contractAddons : undefined,
      })
    },
    [
      validatePayments,
      onChange,
      switches,
      selectedWorkTypes,
      contractPayments,
      b2bPayments,
      contractInputType,
      b2bInputType,
      contractBaseSalary,
      contractAddons,
      state.category,
    ],
  )

  const handleLogOut = useCallback(() => {
    LocalStorageService.removeObject("token")
    dispatch(logout())
  }, [dispatch])

  useEffect(() => {
    if (state.category === "others") {
      if (switches.contract && !contractPayments["all"]) {
        setContractPayments({ all: {} })
      }
      if (switches.b2b && !b2bPayments["all"]) {
        setB2BPayments({ all: {} })
      }
    }
  }, [b2bPayments, contractPayments, state.category, switches])

  return (
    <Modal
      show={true}
      icon="plus-circle"
      title={t("additional-info.title")}
      text={t("additional-info.text")}
      $maxWidth="1100px"
      showBackground={false}
      mobileFullScreen={true}
      helperElement={
        <ChangeLanguageComponent>
          <AddOfferHelperText />
        </ChangeLanguageComponent>
      }
      helperButton={{
        text: t("logout", { ns: "common" }),
        onClick: handleLogOut,
      }}
      bottomElement={
        <Row $justifyContent="flex-end">
          <Button $type="secondary" onClick={onBack} $marginRight="S">
            {t("back", { ns: "common" })}
          </Button>
          <Button onClick={handleSubmit(onSubmit)}>{t("next", { ns: "common" })}</Button>
        </Row>
      }
    >
      <>
        <BasicInfoGrid>
          <P label={t("offer.category", { ns: "form" })} $marginBottom="0">
            {`${t(state.category, { ns: "data" })}, ${t(state.subcategory, { ns: "data" })}`}
          </P>
          <P label={t("address", { ns: "form" })} $marginBottom="0">
            {state.address?.name}
          </P>
        </BasicInfoGrid>
        <BlockWrapper>
          <H3 $marginBottom="0">{t("additional-info.name", { ns: "add-offer" })}</H3>
          <Input
            name="name"
            value={watch("name")}
            error={errors.name}
            rhf={register("name", {
              required: t("required", { ns: "form" }),
            })}
          />
        </BlockWrapper>
        <BlockWrapper>
          <Row $justifyContent="space-between" $alignItems="center" $marginBottom="0">
            <H3 $marginBottom="0">{t("additional-info.price", { ns: "add-offer" })}</H3>
            <CurrencySelectWrapper>
              <Select
                name="paymentCurrency"
                options={CURRENCY_ARRAY.map(el => [el[1].toUpperCase(), el[1]])}
                value={watch("paymentCurrency")}
                rhf={register("paymentCurrency", {
                  required: t("required-all", { ns: "form" }),
                })}
              />
            </CurrencySelectWrapper>
          </Row>
          {state.category === "drivers" && (
            <div>
              <H4 $marginBottom="XS">
                {t("additional-info.select-work-types", { ns: "add-offer" })}
              </H4>
              <CheckboxesGroup
                name="workTypes"
                $type="secondary"
                options={WORKTYPES_DRIVER_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
                value={selectedWorkTypes}
                onChange={handleWorkTypesChange}
              />
              {(errors as any).workTypes && (
                <ErrorText>{(errors as any).workTypes.message as string}</ErrorText>
              )}
            </div>
          )}
          <Box $marginBottom="S">
            <Row>
              <Switch
                name="contract"
                value={switches.contract}
                onChange={onChangePaymentSwitches}
              />
              <Box $marginLeft="S">
                <H4 $marginBottom="XXS">
                  {t("additional-info.contract.title", { ns: "add-offer" })}
                </H4>
                <P $small $marginBottom="0">
                  {t("additional-info.contract.text", { ns: "add-offer" })}
                </P>
              </Box>
            </Row>
            {switches.contract && (
              <Box $marginTop="S">
                {state.category === "drivers" ? (
                  <>
                    <RadioGroup
                      name="contractInputType"
                      options={[
                        [t("additional-info.contract.perMonth", { ns: "add-offer" }), "monthly"],
                        [t("additional-info.contract.perDay", { ns: "add-offer" }), "daily"],
                      ]}
                      value={contractInputType}
                      onChange={({ value }: { value: string }) =>
                        setContractInputType(value as "monthly" | "daily")
                      }
                    />
                    {selectedWorkTypes.map((workType: string) => (
                      <WorkTypePaymentWrapper key={workType}>
                        <WorkTypePaymentBox>
                          <WorkTypeTitle>{t(workType, { ns: "data" })}</WorkTypeTitle>
                          <PaymentInputGrid isLarge>
                            <PaymentInputGroupGrid>
                              <Input
                                name={`contract_${workType}_monthlyFrom`}
                                placeholder={t("additional-info.contract.from", {
                                  ns: "add-offer",
                                })}
                                elementType="numeric"
                                decimalScale={0}
                                value={contractPayments[workType]?.monthlyFrom ?? ""}
                                $background="white"
                                onChange={({ value }) => {
                                  if (contractInputType === "monthly")
                                    handleContractMonthlyChange(workType, "from", value)
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={contractInputType === "daily"}
                              />
                              <Input
                                name={`contract_${workType}_monthlyTo`}
                                placeholder={t("additional-info.contract.to", { ns: "add-offer" })}
                                elementType="numeric"
                                decimalScale={0}
                                value={contractPayments[workType]?.monthlyTo ?? ""}
                                $background="white"
                                onChange={({ value }) => {
                                  if (contractInputType === "monthly")
                                    handleContractMonthlyChange(workType, "to", value)
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={contractInputType === "daily"}
                              />
                            </PaymentInputGroupGrid>
                            <PaymentInputGroupGrid>
                              <Input
                                name={`contract_${workType}_dailyFrom`}
                                placeholder={t("additional-info.contract.perDay", {
                                  ns: "add-offer",
                                })}
                                elementType="numeric"
                                decimalScale={0}
                                value={contractPayments[workType]?.dailyFrom ?? ""}
                                $background="white"
                                onChange={({ value }) => {
                                  if (contractInputType === "daily")
                                    handleContractDailyChange(workType, "from", value)
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={contractInputType === "monthly"}
                              />
                              <Input
                                name={`contract_${workType}_dailyTo`}
                                placeholder={t("additional-info.contract.perDay", {
                                  ns: "add-offer",
                                })}
                                elementType="numeric"
                                decimalScale={0}
                                value={contractPayments[workType]?.dailyTo ?? ""}
                                $background="white"
                                onChange={({ value }) => {
                                  if (contractInputType === "daily")
                                    handleContractDailyChange(workType, "to", value)
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={contractInputType === "monthly"}
                              />
                            </PaymentInputGroupGrid>
                          </PaymentInputGrid>
                        </WorkTypePaymentBox>
                        {(errors as any)[`contract_${workType}`] && (
                          <ErrorText>
                            {(errors as any)[`contract_${workType}`]?.message as string}
                          </ErrorText>
                        )}
                      </WorkTypePaymentWrapper>
                    ))}
                    {state.category === "drivers" && switches.contract && (
                      <Box $marginTop="M">
                        <H4 $marginBottom="XXS">
                          {t("additional-info.contract.baseTitle", { ns: "add-offer" })}
                        </H4>
                        <P $small $marginBottom="S">
                          {t("additional-info.contract.baseText", { ns: "add-offer" })}
                        </P>
                        <Box $marginBottom="M">
                          <Input
                            name="contractBaseSalary"
                            elementType="numeric"
                            decimalScale={0}
                            value={contractBaseSalary}
                            background="white"
                            onChange={({ value }) => setContractBaseSalary(value)}
                            endAdornment={paymentCurrency.toUpperCase()}
                          />
                        </Box>
                        <H4 $marginBottom="XXS">
                          {t("additional-info.contract.addonsTitle", { ns: "add-offer" })}
                        </H4>
                        <P $small $marginBottom="S">
                          {t("additional-info.contract.addonsText", { ns: "add-offer" })}
                        </P>
                        <Row $alignItems="flex-end" $marginBottom="S">
                          <Box $flex={1} $marginRight="S">
                            <Input
                              name="contractAddonInput"
                              placeholder={t("additional-info.contract.addonsPlaceholder", {
                                ns: "add-offer",
                              })}
                              value={contractAddonInput}
                              background="white"
                              onChange={({ value }) => setContractAddonInput(value)}
                            />
                          </Box>
                          <Button
                            onClick={handleAddContractAddon}
                            disabled={!contractAddonInput.trim()}
                          >
                            {t("add", { ns: "common" })}
                          </Button>
                        </Row>
                        {contractAddons.length > 0 && (
                          <AddonsList>
                            {contractAddons.map((addon, index) => (
                              <AddonItem key={index}>
                                <P $marginBottom="0">{addon}</P>
                                <IconButton
                                  $iconSize="M"
                                  $size="35px"
                                  $borderRadius="borderRadius"
                                  $background="backgroundDarker"
                                  onClick={() => handleRemoveContractAddon(index)}
                                  icon="close"
                                  ariaLabel=""
                                />
                              </AddonItem>
                            ))}
                          </AddonsList>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <WorkTypePaymentWrapper>
                    <PaymentInputGrid>
                      <PaymentInputGroupGrid>
                        <Input
                          name="contract_all_monthlyFrom"
                          placeholder={t("additional-info.contract.from", { ns: "add-offer" })}
                          elementType="numeric"
                          decimalScale={0}
                          value={contractPayments["all"]?.monthlyFrom ?? ""}
                          background="white"
                          onChange={({ value }) =>
                            handleContractMonthlyChange("all", "from", value)
                          }
                          endAdornment={paymentCurrency.toUpperCase()}
                        />
                        <Input
                          name="contract_all_monthlyTo"
                          placeholder={t("additional-info.contract.to", { ns: "add-offer" })}
                          elementType="numeric"
                          decimalScale={0}
                          value={contractPayments["all"]?.monthlyTo ?? ""}
                          background="white"
                          onChange={({ value }) => handleContractMonthlyChange("all", "to", value)}
                          endAdornment={paymentCurrency.toUpperCase()}
                        />
                      </PaymentInputGroupGrid>
                    </PaymentInputGrid>
                    {(errors as any).contract_all && (
                      <ErrorText>{(errors as any).contract_all.message as string}</ErrorText>
                    )}
                  </WorkTypePaymentWrapper>
                )}
              </Box>
            )}
          </Box>
          <Box>
            <Row>
              <Switch name="b2b" value={switches.b2b} onChange={onChangePaymentSwitches} />
              <Box $marginLeft="S">
                <H4 $marginBottom="XXS">{t("additional-info.b2b.title", { ns: "add-offer" })}</H4>
                <P $small $marginBottom="0">
                  {t("additional-info.b2b.text", { ns: "add-offer" })}
                </P>
              </Box>
            </Row>
            {switches.b2b && (
              <Box $marginTop="S">
                {state.category === "drivers" ? (
                  <>
                    <RadioGroup
                      name="b2bInputType"
                      options={[
                        [t("additional-info.contract.perMonth", { ns: "add-offer" }), "monthly"],
                        [t("additional-info.contract.perDay", { ns: "add-offer" }), "daily"],
                      ]}
                      value={b2bInputType}
                      onChange={({ value }: { value: string }) =>
                        setB2bInputType(value as "monthly" | "daily")
                      }
                    />
                    {selectedWorkTypes.map((workType: string) => (
                      <WorkTypePaymentWrapper key={workType}>
                        <WorkTypePaymentBox>
                          <WorkTypeTitle>{t(workType, { ns: "data" })}</WorkTypeTitle>
                          <PaymentInputGrid isLarge>
                            <PaymentInputGroupGrid>
                              <Input
                                name={`b2b_${workType}_monthlyFrom`}
                                placeholder={t("additional-info.contract.from", {
                                  ns: "add-offer",
                                })}
                                elementType="numeric"
                                decimalScale={0}
                                value={b2bPayments[workType]?.monthlyFrom ?? ""}
                                $background="white"
                                onChange={(e: any) => {
                                  if (b2bInputType === "monthly") {
                                    handleB2BMonthlyChange(workType, "from", getInputValue(e))
                                  }
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={b2bInputType === "daily"}
                              />
                              <Input
                                name={`b2b_${workType}_monthlyTo`}
                                placeholder={t("additional-info.contract.to", { ns: "add-offer" })}
                                elementType="numeric"
                                decimalScale={0}
                                value={b2bPayments[workType]?.monthlyTo ?? ""}
                                $background="white"
                                onChange={(e: any) => {
                                  if (b2bInputType === "monthly") {
                                    handleB2BMonthlyChange(workType, "to", getInputValue(e))
                                  }
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={b2bInputType === "daily"}
                              />
                            </PaymentInputGroupGrid>
                            <PaymentInputGroupGrid>
                              <Input
                                name={`b2b_${workType}_dailyFrom`}
                                placeholder={t("additional-info.contract.perDay", {
                                  ns: "add-offer",
                                })}
                                elementType="numeric"
                                decimalScale={0}
                                value={b2bPayments[workType]?.dailyFrom ?? ""}
                                $background="white"
                                onChange={(e: any) => {
                                  if (b2bInputType === "daily") {
                                    handleB2BDailyChange(workType, "from", getInputValue(e))
                                  }
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={b2bInputType === "monthly"}
                              />
                              <Input
                                name={`b2b_${workType}_dailyTo`}
                                placeholder={t("additional-info.contract.perDay", {
                                  ns: "add-offer",
                                })}
                                elementType="numeric"
                                decimalScale={0}
                                value={b2bPayments[workType]?.dailyTo ?? ""}
                                $background="white"
                                onChange={(e: any) => {
                                  if (b2bInputType === "daily") {
                                    handleB2BDailyChange(workType, "to", getInputValue(e))
                                  }
                                }}
                                endAdornment={paymentCurrency.toUpperCase()}
                                disabled={b2bInputType === "monthly"}
                              />
                            </PaymentInputGroupGrid>
                          </PaymentInputGrid>
                        </WorkTypePaymentBox>
                        {(errors as any)[`b2b_${workType}`] && (
                          <ErrorText>
                            {(errors as any)[`b2b_${workType}`]?.message as string}
                          </ErrorText>
                        )}
                      </WorkTypePaymentWrapper>
                    ))}
                  </>
                ) : (
                  <WorkTypePaymentWrapper>
                    <PaymentInputGrid>
                      <PaymentInputGroupGrid>
                        <Input
                          name="b2b_all_monthlyFrom"
                          placeholder={t("additional-info.contract.from", { ns: "add-offer" })}
                          elementType="numeric"
                          decimalScale={0}
                          value={b2bPayments["all"]?.monthlyFrom ?? ""}
                          $background="white"
                          onChange={(e: any) =>
                            handleB2BMonthlyChange("all", "from", getInputValue(e))
                          }
                          endAdornment={paymentCurrency.toUpperCase()}
                        />
                        <Input
                          name="b2b_all_monthlyTo"
                          placeholder={t("additional-info.contract.to", { ns: "add-offer" })}
                          elementType="numeric"
                          decimalScale={0}
                          value={b2bPayments["all"]?.monthlyTo ?? ""}
                          $background="white"
                          onChange={(e: any) =>
                            handleB2BMonthlyChange("all", "to", getInputValue(e))
                          }
                          endAdornment={paymentCurrency.toUpperCase()}
                        />
                      </PaymentInputGroupGrid>
                    </PaymentInputGrid>
                    {(errors as any).b2b_all && (
                      <ErrorText>{(errors as any).b2b_all.message as string}</ErrorText>
                    )}
                  </WorkTypePaymentWrapper>
                )}
              </Box>
            )}
          </Box>
        </BlockWrapper>
      </>
    </Modal>
  )
}

export default AddOfferSalaryInfo

const BlockWrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.M} ${({ theme }) => theme.space.S};
  width: calc(100% + (${({ theme }) => theme.space.S} * 2));
  margin-left: -${({ theme }) => theme.space.S};
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.M};

  &.hide_border {
    border-bottom: none;
  }
`

const BasicInfoGrid = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${({ theme }) => theme.space.L};
  grid-row-gap: ${({ theme }) => theme.space.S};
  margin-bottom: ${({ theme }) => theme.space.S};
`

const CurrencySelectWrapper = styled(Box)`
  max-width: 200px;
`

const PaymentInputGrid = styled(Box)<{
  isLarge?: boolean
}>`
  display: grid;
  grid-template-columns: ${({ isLarge }) => (isLarge ? "1fr 1fr" : "1fr")};
  grid-gap: ${({ theme }) => theme.space.XS};
`

const PaymentInputGroupGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: 50px;
  overflow: hidden;
  grid-gap: 2px;

  .MuiInputBase-root {
    border: none !important;
  }
`

const WorkTypePaymentBox = styled(Box)`
  display: grid;
  grid-template-columns: max-content auto;
  grid-gap: ${({ theme }) => theme.space.S};
  align-items: center;
`

const WorkTypePaymentWrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  margin-top: ${({ theme }) => theme.space.XS};
  background: ${({ theme }) => theme.color.background};
`

const WorkTypeTitle = styled(P)`
  font-weight: 600;
  margin-bottom: 0;
`

const ErrorText = styled(P)`
  margin-bottom: 0;
  margin-top: ${({ theme }) => theme.space.XS};
  color: ${({ theme }) => theme.color.red} !important;
  font-size: ${({ theme }) => theme.font.S};
`

const AddonsList = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XXS};
  margin-top: ${({ theme }) => theme.space.S};
`

const AddonItem = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.space.XXS};
  padding-left: ${({ theme }) => theme.space.S};
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
`
